import {
  expandSlotsToThirtyMinuteChoices,
  formatScheduledAt,
  formatSlotRangeInTimezone,
  formatTimezoneAbbr,
} from '../../../../helpers/timezone_utils';
import { maskPhoneNumber } from '../../../../helpers';

const ONE_HOUR_MS = 60 * 60 * 1000;

function capitalize(name) {
  if (!name) return name;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function describeActor(actorType, { isMentor, otherUserName }) {
  if (!actorType) return null;
  if (actorType === 'Patient') return isMentor ? otherUserName || 'Your mentee' : 'You';
  if (actorType === 'PatientPartner') return isMentor ? 'You' : otherUserName || 'Your mentor';
  return null;
}

function isSlotBlocked(slot, blockedSlots) {
  if (!blockedSlots?.length) return false;
  const slotStart = new Date(slot.start_at).getTime();
  return blockedSlots.some(({ start_at }) => Math.abs(slotStart - new Date(start_at).getTime()) < ONE_HOUR_MS);
}

function buildSlots({ call, viewerTimezone, tzAbbr, blockedSlots, onAcceptSlot, interactive }) {
  if (!call.scheduled || !call.proposed_slots?.length) return [];
  const choices = expandSlotsToThirtyMinuteChoices(call.proposed_slots);
  return choices.map((choice) => {
    const blocked = isSlotBlocked(choice, blockedSlots);
    const endISO = choice.end_at || new Date(new Date(choice.start_at).getTime() + 30 * 60 * 1000).toISOString();
    const label = formatSlotRangeInTimezone(choice.start_at, endISO, viewerTimezone, tzAbbr);
    const shouldWire = interactive && !blocked && call.actionable && typeof onAcceptSlot === 'function';
    return {
      key: choice.start_at,
      label: blocked ? `${label} • Booked` : label,
      onAccept: shouldWire ? () => onAcceptSlot(choice) : undefined,
    };
  });
}

export function buildCallEventCardProps({
  call,
  isMentor,
  viewerTimezone,
  viewerPhone,
  otherUserFirstName,
  blockedSlots = [],
  onAcceptSlot,
  onAccept,
  onCancel,
  onCounter,
  onSchedule,
}) {
  if (!call) return null;

  const tz = viewerTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzAbbr = formatTimezoneAbbr(tz);
  const otherName = otherUserFirstName || (isMentor ? 'your mentee' : 'your mentor');
  const otherNameCap = capitalize(otherName);

  const {
    status,
    scheduled,
    scheduled_at,
    proposed_slots,
    countered_at,
    countered_by_type,
    declined_by_type,
    canceled_by_type,
    accepted_by_type,
    cancelable,
    actionable,
    acceptance_expires_at,
  } = call;

  const counteredBy = (type) => Boolean(countered_at) && countered_by_type === type;
  const counteredByOther = counteredBy(isMentor ? 'Patient' : 'PatientPartner');

  const cancelHandler = cancelable && typeof onCancel === 'function' ? onCancel : undefined;
  const counterHandler = actionable && typeof onCounter === 'function' ? onCounter : undefined;
  const acceptHandler = actionable && typeof onAccept === 'function' ? onAccept : undefined;

  if (status === 'in_progress') {
    return {
      tone: 'accepted',
      title: 'Call in progress',
      description: 'Stay on the line — your call is happening now.',
    };
  }

  if (status === 'completed') {
    return {
      tone: 'accepted',
      title: 'Call completed',
      description: `Your call with ${otherName} has ended.`,
    };
  }

  if (status === 'accepted') {
    const viewerAccepted =
      (isMentor && accepted_by_type === 'PatientPartner') || (!isMentor && accepted_by_type === 'Patient');
    const acceptorLabel = viewerAccepted ? 'You' : otherNameCap;
    const maskedPhone = maskPhoneNumber(viewerPhone);
    const description = scheduled_at
      ? `Scheduled for ${formatScheduledAt(scheduled_at, tz)}.`
      : maskedPhone
        ? `${acceptorLabel} accepted, we'll call you shortly at ${maskedPhone}.`
        : `${acceptorLabel} accepted, we'll call you shortly.`;
    return {
      tone: 'accepted',
      title: 'Call confirmed',
      description,
      slots:
        scheduled_at && proposed_slots?.length
          ? [{ key: 'scheduled', label: formatScheduledAt(scheduled_at, tz) }]
          : [],
      onCancel: cancelHandler,
    };
  }

  if (status === 'declined') {
    const actor = describeActor(declined_by_type, { isMentor, otherUserName: otherNameCap });
    const declinedBySelf = actor === 'You';
    const scheduleHandler = typeof onSchedule === 'function' ? onSchedule : undefined;
    return {
      tone: 'declined',
      title: actor ? `${actor} declined the call` : 'Call declined',
      description: declinedBySelf
        ? `You let ${otherName} know this time doesn't work. They can suggest a new time.`
        : `${otherNameCap} let you know this time doesn't work.`,
      actionLink:
        !declinedBySelf && scheduleHandler
          ? { label: 'Click here to schedule a call instead', onClick: scheduleHandler }
          : undefined,
    };
  }

  if (status === 'canceled') {
    const actor = describeActor(canceled_by_type, { isMentor, otherUserName: otherNameCap });
    return {
      tone: 'declined',
      title: actor ? `${actor} canceled the call` : 'Call canceled',
      description: 'The call was canceled.',
    };
  }

  if (status === 'expired') {
    return {
      tone: 'declined',
      title: 'Call request expired',
      description: `Your call request with ${otherName} expired. Please try again.`,
    };
  }

  if (status === 'mentor_unreachable') {
    return {
      tone: 'declined',
      title: isMentor ? 'We couldn’t reach you for the call' : `We couldn’t reach ${otherName}`,
      description: 'Please try again later.',
    };
  }

  if (status === 'patient_unreachable') {
    return {
      tone: 'declined',
      title: isMentor ? `We couldn’t reach ${otherName}` : 'We couldn’t reach you for the call',
      description: 'Please try again later.',
    };
  }

  if (status === 'pending') {
    if (counteredByOther && scheduled) {
      return {
        tone: 'waiting',
        title: `${otherNameCap} proposed new times`,
        description: 'Pick a time that works for you.',
        slots: buildSlots({
          call,
          viewerTimezone: tz,
          tzAbbr,
          blockedSlots,
          onAcceptSlot,
          interactive: true,
        }),
        onCancel: cancelHandler,
        onCounter: counterHandler,
        counterLabel: 'Propose new times',
        expiresAt: acceptance_expires_at,
      };
    }

    if (isMentor && scheduled && !counteredBy('PatientPartner')) {
      return {
        tone: 'waiting',
        title: `${otherNameCap} requested a call`,
        description: 'Pick a time that works for you.',
        slots: buildSlots({
          call,
          viewerTimezone: tz,
          tzAbbr,
          blockedSlots,
          onAcceptSlot,
          interactive: true,
        }),
        onCancel: cancelHandler,
        cancelLabel: 'Decline',
        cancelConfirmTitle: 'Decline this call request?',
        cancelConfirmYesLabel: 'Yes, decline',
        onCounter: counterHandler,
        counterLabel: 'Propose new times',
        expiresAt: acceptance_expires_at,
      };
    }

    if (isMentor && !scheduled && !counteredBy('PatientPartner')) {
      return {
        tone: 'waiting',
        title: `${otherNameCap} requested a call`,
        description: 'Waiting for you to accept the call.',
        onAccept: acceptHandler,
        acceptButtonLabel: 'Accept call',
        onCancel: cancelHandler,
        cancelLabel: 'Decline',
        cancelConfirmTitle: 'Decline this call request?',
        cancelConfirmYesLabel: 'Yes, decline',
        expiresAt: acceptance_expires_at,
      };
    }

    const proposedCount = proposed_slots?.length || 0;
    const waitingDescription = scheduled
      ? proposedCount === 1
        ? `You proposed 1 time. Waiting for ${otherName} to confirm.`
        : `You proposed ${proposedCount} times. Waiting for ${otherName} to confirm one.`
      : `Waiting for ${otherName}. You can close the app, just keep your phone on and we'll ring you when they're ready`;

    return {
      tone: 'waiting',
      title: 'Waiting for confirmation',
      description: waitingDescription,
      slots: buildSlots({
        call,
        viewerTimezone: tz,
        tzAbbr,
        blockedSlots,
        onAcceptSlot,
        interactive: false,
      }),
      onCancel: cancelHandler,
      expiresAt: acceptance_expires_at,
    };
  }

  return null;
}
